import { useCallback, useState } from 'react';
import {
  AritoIcon,
  BulkDeleteDialog,
  ExcelImportDialog,
  ExcelImportErrorDialog,
  ErpDataTableV2
} from '@/components/custom/erp';
import { useCRUD, useExcel, useExcelProcessor, useForms, useFormState, useRows } from '@/hooks';
import { ActionBar, ConfirmDialog, FormDialog } from './components';
import { buildFeePayload, ExcelConfig, ImportBody } from './utils';
import { ColumnMapping, ValidationError } from '@/lib/excel';
import { Phi, PhiInput } from '@/types/schemas';
import { feeColumns } from './cols-definition';
import { toast } from '@/hooks/use-toast';
import { QUERY_KEYS } from '@/constants';
import { useSearchTable } from './hooks';

const PAGE_SIZE = 50;

export default function DanhMucYeuToPage() {
  const {
    data: fees,
    isLoading,
    addItem: addFee,
    updateItem: updateFee,
    deleteItem: deleteFee,
    refreshData: refreshFees,
    totalItems,
    currentPage,
    handlePageChange
  } = useCRUD<Phi, PhiInput>({
    endpoint: QUERY_KEYS.PHI,
    pageSize: PAGE_SIZE
  });
  const { getUUIDByCode } = useExcel();
  const { processFile } = useExcelProcessor(ExcelConfig);

  const [isLoadingImport, setIsLoadingImport] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorData, setErrorData] = useState<{
    errors: ValidationError[];
    columnMapping: Record<string, ColumnMapping>;
  } | null>(null);

  const { selectedObj, selectedRowIndex, handleRowClick, clearSelection } = useRows<Phi>();
  const {
    showForm,
    showDelete,
    formMode,

    isCopyMode,

    handleCloseForm,
    handleCloseDelete,
    handleAddClick,
    handleEditClick,
    handleViewClick,
    handleDeleteClick,
    handleCopyClick
  } = useFormState<Phi>();
  const { showBulkDelete, handleCloseBulkDelete, isImportDialogOpen, setIsImportDialogOpen, handleOpenImportExcel } =
    useForms<Phi>();

  const {
    searchState,
    handleSearchChange,
    onPageChange,
    hasFilterParams,
    handleRefreshClick: handleDynamicRefresh,
    isFiltering
  } = useSearchTable({
    handlePageChange
  });

  const handleSubmit = async (data: PhiInput) => {
    console.log('Submitting data:', data);
    try {
      if (formMode === 'add') {
        await addFee(data);
      } else if (formMode === 'edit' && selectedObj) {
        await updateFee(selectedObj.uuid, data);
      }

      handleCloseForm();
      clearSelection();
      await refreshFees();
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  const handleImportBodies = async (bodies: ImportBody[]) => {
    let successCount = 0;
    setIsLoadingImport(true);
    try {
      for (const body of bodies) {
        await addFee(buildFeePayload(body));
        successCount++;
      }
      toast({
        description: 'Đã nhập danh mục phí từ Excel thành công',
        variant: 'default'
      });
    } catch (error) {
      console.error('Error importing fees from Excel:', error);
      toast({
        description: 'Có lỗi xảy ra khi nhập danh mục phí từ Excel',
        variant: 'destructive'
      });
      throw error;
    } finally {
      await refreshFees();
      setIsLoadingImport(false);
    }
  };

  const handleImportFromExcel = async (data: { errors?: string; file: File | null; sheetIndex?: string }) => {
    if (!data.file) {
      toast({
        description: 'Vui lòng chọn file Excel',
        variant: 'destructive'
      });
      return;
    }

    const maxErrors = parseInt(data.errors || '200', 10);
    const sheetIndex = parseInt(data.sheetIndex || '0', 10);

    const result = await processFile({
      file: data.file,
      sheetIndex,
      maxErrors,
      getUUIDByCode,
      context: {}
    });

    if (result.success && result.data) {
      await handleImportBodies(result.data as ImportBody[]);
    } else if (result.errors && result.columnMapping) {
      setErrorData({ errors: result.errors, columnMapping: result.columnMapping });
      setShowErrorDialog(true);
    }
  };

  const handleRefresh = useCallback(async () => {
    if (hasFilterParams) {
      await handleDynamicRefresh();
    } else {
      await refreshFees();
    }
  }, [hasFilterParams, handleDynamicRefresh, refreshFees]);

  const displayData = hasFilterParams ? searchState.searchResults : fees;
  const resolvedTotalItems = hasFilterParams ? searchState.totalCount : totalItems;
  const resolvedCurrentPage = hasFilterParams ? searchState.currentPage : currentPage;
  const isTableLoading = isLoading || isFiltering;

  const tables = [
    {
      name: '',
      rows: displayData,
      columns: feeColumns
    }
  ];

  return (
    <div className='flex h-screen flex-col lg:overflow-hidden'>
      {showForm && (
        <FormDialog
          open={showForm}
          onSubmit={handleSubmit}
          formMode={formMode}
          initialData={
            selectedObj && (formMode === 'edit' || formMode === 'view' || (formMode === 'add' && isCopyMode))
              ? selectedObj
              : formMode === 'add' && !isCopyMode
                ? undefined
                : undefined
          }
          onClose={handleCloseForm}
          onAdd={() => {
            handleCloseForm();
            clearSelection();
            setTimeout(() => {
              handleAddClick();
            }, 100);
          }}
          onEdit={() => {
            if (selectedObj) {
              handleCloseForm();
              handleEditClick();
            }
          }}
          onDelete={() => {
            if (selectedObj) {
              handleCloseForm();
              handleDeleteClick();
            }
          }}
          onCopy={() => {
            if (selectedObj) {
              handleCloseForm();
              handleCopyClick();
            }
          }}
        />
      )}

      {showDelete && (
        <ConfirmDialog
          open={showDelete}
          onClose={handleCloseDelete}
          selectedObj={selectedObj}
          onDelete={deleteFee}
          clearSelection={clearSelection}
          title='Xoá dữ liệu'
          content='Bạn có chắc chắn xoá không?'
        />
      )}
      {showBulkDelete && (
        <BulkDeleteDialog
          title='Phí'
          onClose={handleCloseBulkDelete}
          columns={feeColumns}
          dataRows={displayData}
          handleDelete={deleteFee}
        />
      )}

      <ActionBar
        onAddClick={handleAddClick}
        onEditClick={() => selectedObj && handleEditClick()}
        onCopyClick={() => selectedObj && handleCopyClick()}
        onDeleteClick={() => selectedObj && handleDeleteClick()}
        onViewClick={() => selectedObj && handleViewClick()}
        onRefreshClick={handleRefresh}
        onFixedColumnsClick={() => {}}
        onExportClick={() => {}}
        onImportExcel={handleOpenImportExcel}
        isViewDisabled={!selectedObj}
      />

      <div data-excel-operation-overlay-root className='min-h-0 flex-1 overflow-hidden'>
        <ErpDataTableV2
          tables={tables}
          selectedRowId={selectedRowIndex || undefined}
          onRowClick={handleRowClick}
          totalItems={resolvedTotalItems}
          currentPage={resolvedCurrentPage}
          onPageChange={onPageChange}
          pageSize={PAGE_SIZE}
          onSearchChange={handleSearchChange}
          loading={isTableLoading}
        />
      </div>

      {isImportDialogOpen && (
        <ExcelImportDialog
          open={isImportDialogOpen}
          onClose={() => setIsImportDialogOpen(false)}
          onSubmit={handleImportFromExcel}
          title='Lấy dữ liệu từ excel'
          titleIcon={<AritoIcon icon={555} />}
        />
      )}

      {showErrorDialog && errorData && (
        <ExcelImportErrorDialog
          open={showErrorDialog}
          onClose={() => {
            setShowErrorDialog(false);
            setErrorData(null);
          }}
          errors={errorData.errors}
          columnMapping={errorData.columnMapping}
        />
      )}
    </div>
  );
}
